import { useMemo, useSyncExternalStore } from 'react';

import {
  getActiveLocale,
  subscribeToActiveLocale,
  translateWithLocale,
  type TranslateOptions,
  type TranslationFileKey,
  type TranslationKey,
  type TranslationNamespace,
} from '@/i18n';

type FileTranslateFunction<Namespace extends TranslationNamespace> = (
  key: TranslationFileKey<Namespace>,
  options?: TranslateOptions,
) => string;

/**
 * Shortcut for components that only need to translate. Pass the namespace that
 * mirrors the source file so the available keys are limited to its catalogue.
 *
 * It subscribes to the locale store, not to this context: a component
 * re-renders on a language change wherever it sits in the tree, `memo` does not
 * block it, and it reads exactly what a util reading `getActiveLocale()` would
 * read in the same render. It therefore needs no provider — which is also what
 * keeps a component rendered in isolation working.
 */
export function useTranslate<Namespace extends TranslationNamespace>(
  namespace: Namespace,
): FileTranslateFunction<Namespace> {
  const locale = useSyncExternalStore(subscribeToActiveLocale, getActiveLocale, getActiveLocale);

  // Stable while the locale and the namespace are: callers put `t` in
  // `useCallback` and `useEffect` dependency arrays, and a new function on
  // every render would restart the effects that depend on them.
  return useMemo(
    () => (key: TranslationFileKey<Namespace>, options?: TranslateOptions) =>
      translateWithLocale(locale, `${namespace}.${key}` as TranslationKey, options),
    [locale, namespace],
  );
}
